/**
 * WorkforceRequests — bank-persona interaction atlas §4 (R1–R5), stage 5 of
 * the atlas port. One queue for other people's time and one for the manager's
 * own; the fourth tab existing at all is the design insight — a branch manager
 * is also on the roster, and cannot approve themselves.
 *
 * Ops: listWorkforceRequests / decideWorkforceRequest · listMarketplaceShifts
 * (open shifts) · getMyTime (the manager's own requests).
 *
 * Held pending the data layer (REQ-101): counterparty state on a swap, Nudge,
 * average time to decision, who decided a settled request, the history window,
 * and Post open shift.
 */
import { useMemo, useState } from "react";
import { Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, EmptyState } from "../../shell/composites";
import { thStyle, tdStyle } from "../events/EventCard";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useViewport } from "../../shell/useResponsive";
import { friendlyDateShort } from "../../lib/datetime";
import { useWfResource, useBusy } from "./useWorkforce";
import { listWorkforceRequests, decideWorkforceRequest, listMarketplaceShifts, getMyTime, type SwapRequest, type TimeOffRequest, type WfRequest } from "./wf-api";
import { WfPending, requestStatusPill, RuleFlags, dayLabel, hmRangeLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const TIMEOFF_LABEL: Record<TimeOffRequest["type"], string> = { vacation: "Vacation", sick: "Sick", personal: "Personal", other: "Other" };
type Tab = "approvals" | "open" | "mytime" | "history";

export default function WorkforceRequests() {
  const t = useT();
  const nav = useNav();
  const phone = useViewport() === "phone";
  const [tab, setTab] = useState<Tab>("approvals"); // ui-local
  const [decided, setDecided] = useState<Record<string, WfRequest>>({}); // server-confirmed replacements
  const { busy, run } = useBusy();
  const canDecide = can("swaps.approve");

  const { data, loading, error, reload } = useWfResource<{ swaps: SwapRequest[]; timeoff: TimeOffRequest[] }>("wfrequests", () => listWorkforceRequests());
  const market = useWfResource("wfreq-market", () => listMarketplaceShifts());
  const mine = useWfResource("wfreq-mytime", getMyTime);

  const swaps = useMemo(() => (data?.swaps ?? []).map((r) => (decided[r.id] as SwapRequest) ?? r), [data, decided]);
  const timeoff = useMemo(() => (data?.timeoff ?? []).map((r) => (decided[r.id] as TimeOffRequest) ?? r), [data, decided]);
  const pending = [...swaps, ...timeoff].filter((r) => r.status === "pending");
  const settled = [...swaps, ...timeoff].filter((r) => r.status !== "pending");
  const openShifts = (market.data?.shifts ?? []).filter((s) => s.status === "open");
  const ptoLeft = mine.data?.kpis?.ptoBalanceDays ?? null;

  const decide = async (r: WfRequest, decision: "approved" | "declined") => {
    await run(`${r.id}:${decision}`, async () => {
      try {
        const res = await decideWorkforceRequest(r.id, decision);
        setDecided((m) => ({ ...m, [r.id]: res }));
        toast({ tone: "success", title: decision === "approved" ? t("workforce.requests.requestApproved", "Request approved") : t("workforce.requests.requestDeclined", "Request declined") });
      } catch (e) {
        toast({ tone: "danger", title: t("workforce.requests.decisionDidnTSave", "Decision didn't save"), subtitle: e instanceof Error ? e.message : String(e) });
      }
    });
  };

  const decideBtns = (r: WfRequest) => {
    if (r.status !== "pending" || !canDecide) return requestStatusPill(r.status);
    const busyA = busy === `${r.id}:approved`, busyD = busy === `${r.id}:declined`;
    const base = { padding: "7px 12px", minHeight: 34, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, border: `1px solid ${T.border}`, boxShadow: T.shadowChrome } as const;
    return (
      <span style={{ display: "inline-flex", gap: 6 }}>
        <button data-testid="wfrequests-decline-action" data-id={r.id} disabled={busy !== null} onClick={() => decide(r, "declined")}
          style={{ ...base, background: T.white, color: T.n1 }}>{busyD ? "…" : t("workforce.myTime.decline", "Decline")}</button>
        <button data-testid="wfrequests-approve-action" data-id={r.id} disabled={busy !== null} onClick={() => decide(r, "approved")}
          style={{ ...base, background: T.p1, borderColor: T.p1, color: T.white }}>{busyA ? "…" : t("approvals.inbox.approve", "Approve")}</button>
      </span>
    );
  };

  const TABS: { id: Tab; label: string; count?: number }[] = [
    { id: "approvals", label: t("workforce.requests.approvals", "Approvals"), count: pending.length },
    { id: "open", label: t("workforce.requests.openShifts", "Open shifts"), count: openShifts.length },
    { id: "mytime", label: t("workforce.requests.myTime", "My time") },
    { id: "history", label: t("workforce.requests.history", "History") },
  ];

  const section = (title: string, sub: string, body: React.ReactNode, right?: React.ReactNode) => (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowSm, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <b style={{ fontSize: 14.5, color: T.n1 }}>{title}</b>
          <span style={{ fontSize: 11.5, color: T.soft, maxWidth: 620, lineHeight: 1.45 }}>{sub}</span>
        </span>
        {right}
      </div>
      <div style={{ overflowX: "auto" }}>{body}</div>
    </div>
  );

  return (
    <Screen rail="wf-requests">
      <DataSourceStrip screen="wfrequests" ops={["listWorkforceRequests", "decideWorkforceRequest", "listMarketplaceShifts", "getMyTime"]} />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 4, background: T.n6, borderRadius: 9, padding: 3, alignSelf: "flex-start", flexWrap: "wrap" }}>
          {TABS.map((x) => (
            <button key={x.id} onClick={() => setTab(x.id)} data-testid={`wfrequests-tab-${x.id}`}
              style={{ padding: "7px 15px", minHeight: 34, borderRadius: 7, border: "none", background: tab === x.id ? T.p1 : "transparent", color: tab === x.id ? T.white : T.soft, fontSize: 12.5, fontWeight: tab === x.id ? 700 : 600, cursor: "pointer", fontFamily: T.font }}>
              {x.label}{x.count != null ? <span style={{ marginLeft: 5, fontFamily: T.fontMono, fontSize: 11, opacity: 0.85 }}>{x.count}</span> : null}
            </button>
          ))}
        </div>

        {/* R1 — five KPIs. Two of them have nothing behind them yet and say so. */}
        <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr 1fr" : "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
          <StatTile label={t("workforce.requests.awaitingMyApproval", "Awaiting my approval")} value={pending.length} icon={<Icon.check size={14} />} accent={T.p1} />
          <StatTile label={t("workforce.requests.awaitingCounterparty", "Awaiting counterparty")} value={"—"} icon={<Icon.refresh size={14} />} accent={T.n4} />
          <StatTile label={t("workforce.requests.openShiftsIvePosted", "Open shifts I've posted")} value={openShifts.length} icon={<Icon.calendar size={14} />} accent={T.tDarkBlue} />
          <StatTile label={t("workforce.requests.avgTimeToDecision", "Avg time to decision")} value={"—"} icon={<Icon.clock size={14} />} accent={T.n4} />
          <StatTile label={t("workforce.requests.myPtoBalance", "My PTO balance")} value={ptoLeft != null ? `${ptoLeft}d` : "—"} icon={<Icon.user size={14} />} accent={ptoLeft != null ? T.green1 : T.n4} />
        </div>

        {error ? <WfPending screen="wfrequests" opName="listWorkforceRequests" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.requests.loadingRequests", "Loading requests…")}</div>
          : tab === "approvals" ? (
            <>
              {section(
                t("workforce.requests.timeOffAndSick", "Time off and sick"),
                t("workforce.requests.approvingReassigns", "Approving reassigns any affected jrni bookings. PTO and sick share one queue because the decision is the same shape."),
                timeoff.filter((r) => r.status === "pending").length === 0 && timeoff.length === 0
                  ? <EmptyState icon={<Icon.calendar size={20} />} title={t("workforce.requests.noTimeOffRequests", "No time-off requests")} body={t("workforce.requests.nothingWaitingOnADecision", "Nothing waiting on a decision.")} />
                  : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>
                        <th style={thStyle}>{t("workforce.requests.who", "Who")}</th><th style={thStyle}>{t("workforce.requests.type", "Type")}</th><th style={thStyle}>{t("workforce.requests.dates", "Dates")}</th><th style={thStyle}>{t("workforce.requests.balance", "Balance")}</th><th style={thStyle}>{t("workforce.requests.checks", "Checks")}</th><th style={{ ...thStyle, textAlign: "right" }}>{t("workforce.requests.decision", "Decision")}</th>
                      </tr></thead>
                      <tbody>
                        {timeoff.map((r) => (
                          <tr key={r.id} data-testid="wfrequests-timeoff-row" data-id={r.id}>
                            <td style={tdStyle}>
                              <span style={{ fontWeight: 600, color: T.n1, display: "block" }}>{r.staffName}</span>
                              {r.note ? <span style={{ fontSize: 12, color: T.soft }}>“{r.note}”</span> : null}
                            </td>
                            <td style={tdStyle}>{TIMEOFF_LABEL[r.type]}</td>
                            <td style={tdStyle}>{dayLabel(r.startDate, "md")}{r.endDate !== r.startDate ? ` – ${dayLabel(r.endDate, "md")}` : ""}{r.allDay ? "" : ` (${t("workforce.requests.partialDay", "partial day")})`}</td>
                            <td style={tdStyle}><span style={{ fontFamily: T.fontMono }}>{r.balanceDaysLeft != null ? `${r.balanceDaysLeft}d ${t("workforce.requests.left", "left")}` : "—"}</span></td>
                            <td style={tdStyle}>{r.ruleFlags.length ? <RuleFlags flags={r.ruleFlags} /> : <span style={{ fontSize: 12, color: T.green1, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.check size={12} /> {t("common.actions.clear", "Clear")}</span>}</td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>{decideBtns(r)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ),
              )}
              {section(
                t("workforce.requests.shiftSwaps", "Shift swaps"),
                t("workforce.requests.swapsSub", "Manager approval is required only when a swap crosses skill levels, breaks min-rest or triggers overtime — otherwise advisors settle it between themselves."),
                swaps.length === 0
                  ? <EmptyState icon={<Icon.refresh size={20} />} title={t("workforce.requests.noSwapRequests", "No swap requests")} body={t("workforce.requests.nothingWaitingOnADecision", "Nothing waiting on a decision.")} />
                  : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>
                        <th style={thStyle}>{t("workforce.requests.request", "Request")}</th><th style={thStyle}>{t("workforce.requests.givesTakes", "Gives / takes")}</th><th style={thStyle}>{t("workforce.requests.checks", "Checks")}</th><th style={thStyle}>{t("workforce.requests.submitted", "Submitted")}</th><th style={{ ...thStyle, textAlign: "right" }}>{t("workforce.requests.decision", "Decision")}</th>
                      </tr></thead>
                      <tbody>
                        {swaps.map((r) => (
                          <tr key={r.id} data-testid="wfrequests-swap-row" data-id={r.id}>
                            <td style={tdStyle}>
                              <span style={{ fontWeight: 600, color: T.n1, display: "block" }}>
                                {r.kind === "claim" ? `${r.from.name} ${t("workforce.requests.wantsToClaim", "wants to claim an open shift")}` : r.to ? `${r.from.name} → ${r.to.name}` : `${r.from.name} ${t("workforce.requests.offersAShiftUp", "offers a shift up")}`}
                              </span>
                              {r.reason ? <span style={{ fontSize: 12, color: T.soft }}>“{r.reason}”</span> : null}
                            </td>
                            <td style={{ ...tdStyle, fontFamily: T.fontMono, fontSize: 12 }}>{r.role} · {dayLabel(r.date, "full")} · {hmRangeLabel(r.start, r.end)}</td>
                            <td style={tdStyle}>{r.ruleFlags.length ? <RuleFlags flags={r.ruleFlags} /> : <span style={{ fontSize: 12, color: T.green1, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.check size={12} /> {t("common.actions.clear", "Clear")}</span>}</td>
                            <td style={tdStyle}><span style={{ color: T.soft }}>{friendlyDateShort(r.createdAt)}</span></td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>{decideBtns(r)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ),
              )}
              <span style={{ fontSize: 11.5, color: T.soft }}>
                {t("workforce.requests.counterpartyHeld", "A swap's counterparty state (awaiting counterparty vs awaiting manager) and the Nudge action aren't served — the request record carries one status (REQ-101a).")}
              </span>
            </>
          ) : tab === "open" ? (
            section(
              t("workforce.requests.openShifts", "Open shifts"),
              t("workforce.requests.openShiftsSub", "Premium pay is a lever — it exists to make an unattractive shift worth claiming. The claims count is why you come back to this tab."),
              market.loading ? <div style={{ padding: 30, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
                : openShifts.length === 0 ? <EmptyState icon={<Icon.calendar size={20} />} title={t("workforce.requests.noOpenShifts", "No open shifts")} body={t("workforce.requests.nothingPostedRightNow", "Nothing posted right now.")} />
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr>
                      <th style={thStyle}>{t("workforce.requests.when", "When")}</th><th style={thStyle}>{t("workforce.requests.detail", "Detail")}</th><th style={thStyle}>{t("workforce.requests.branch", "Branch")}</th><th style={thStyle}>{t("workforce.requests.premium", "Premium")}</th><th style={{ ...thStyle, textAlign: "right" }}>{t("workforce.requests.claims", "Claims")}</th>
                    </tr></thead>
                    <tbody>
                      {openShifts.map((s) => (
                        <tr key={s.id} data-testid="wfrequests-open-row" data-id={s.id}>
                          <td style={tdStyle}><span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{dayLabel(s.date, "wk-d")} · {hmRangeLabel(s.start, s.end)}</span></td>
                          <td style={tdStyle}><b style={{ color: T.n1 }}>{s.role}</b>{s.reason ? <span style={{ fontSize: 12, color: T.soft, display: "block" }}>{s.reason}</span> : null}</td>
                          <td style={tdStyle}>{s.branch}</td>
                          <td style={tdStyle}>{s.premiumPct > 0 ? <Pill tone="warn">+{s.premiumPct}%</Pill> : <span style={{ color: T.soft }}>—</span>}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            <span style={{ fontFamily: T.fontMono, fontWeight: 700, color: s.claims.length > 0 ? T.green1 : T.soft }}>{s.claims.length}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ),
              <Btn size="sm" disabled icon={<Icon.plus size={13} />} title={t("workforce.requests.postOpenShiftHeld", "Posting an open shift needs REQ-101d")}>{t("workforce.requests.postOpenShift", "Post open shift")}</Btn>,
            )
          ) : tab === "mytime" ? (
            section(
              t("workforce.requests.myTime", "My time"),
              t("workforce.requests.myTimeSub", "You're on the roster too — these go to your regional lead, because a manager cannot approve their own time off."),
              mine.loading ? <div style={{ padding: 30, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
                : (mine.data?.myRequests ?? []).length === 0 ? <EmptyState icon={<Icon.user size={20} />} title={t("workforce.requests.nothingOfYourOwn", "Nothing of your own outstanding")} body={t("workforce.requests.yourRequestsAppearHere", "Your time off, swaps and sick days appear here.")} />
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr>
                      <th style={thStyle}>{t("workforce.requests.request", "Request")}</th><th style={thStyle}>{t("workforce.requests.dates", "Dates")}</th><th style={{ ...thStyle, textAlign: "right" }}>{t("workforce.requests.status", "Status")}</th>
                    </tr></thead>
                    <tbody>
                      {(mine.data?.myRequests ?? []).map((r) => (
                        <tr key={r.id} data-testid="wfrequests-mine-row" data-id={r.id}>
                          <td style={tdStyle}><b style={{ color: T.n1 }}>{r.kind === "timeoff" ? TIMEOFF_LABEL[r.type] : t("workforce.requests.shiftSwap", "Shift swap")}</b></td>
                          <td style={{ ...tdStyle, fontFamily: T.fontMono, fontSize: 12 }}>{r.kind === "timeoff" ? `${dayLabel(r.startDate, "md")}${r.endDate !== r.startDate ? ` – ${dayLabel(r.endDate, "md")}` : ""}` : `${dayLabel(r.date, "md")} · ${hmRangeLabel(r.start, r.end)}`}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>{requestStatusPill(r.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ),
              <Btn size="sm" variant="secondary" onClick={() => nav.go("workforce.scheduletime.sandbox", { tab: "timeoff" })}>{t("workforce.requests.manageMyTime", "Request time off · swap · report sick")} →</Btn>,
            )
          ) : (
            section(
              t("workforce.requests.decisionHistory", "Decision history"),
              t("workforce.requests.historySub", "Settled requests, with the reason where one was given. This is the fairness record you cite when challenged."),
              settled.length === 0 ? <EmptyState icon={<Icon.check size={20} />} title={t("workforce.requests.nothingSettledYet", "Nothing settled yet")} body={t("workforce.requests.decidedRequestsAppearHere", "Approved and declined requests appear here.")} />
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr>
                      <th style={thStyle}>{t("workforce.requests.request", "Request")}</th><th style={thStyle}>{t("workforce.requests.dates", "Dates")}</th><th style={thStyle}>{t("workforce.requests.reason", "Reason")}</th><th style={thStyle}>{t("workforce.requests.decidedBy", "Decided by")}</th><th style={{ ...thStyle, textAlign: "right" }}>{t("workforce.requests.outcome", "Outcome")}</th>
                    </tr></thead>
                    <tbody>
                      {settled.map((r) => (
                        <tr key={r.id} data-testid="wfrequests-history-row" data-id={r.id}>
                          <td style={tdStyle}><b style={{ color: T.n1 }}>{r.kind === "timeoff" ? `${r.staffName} · ${TIMEOFF_LABEL[r.type]}` : `${r.from.name}${r.to ? ` → ${r.to.name}` : ""}`}</b></td>
                          <td style={{ ...tdStyle, fontFamily: T.fontMono, fontSize: 12 }}>{r.kind === "timeoff" ? `${dayLabel(r.startDate, "md")}${r.endDate !== r.startDate ? ` – ${dayLabel(r.endDate, "md")}` : ""}` : `${dayLabel(r.date, "md")} · ${hmRangeLabel(r.start, r.end)}`}</td>
                          <td style={tdStyle}><span style={{ color: T.soft }}>{(r.kind === "timeoff" ? r.note : r.reason) || "—"}</span></td>
                          <td style={tdStyle}><span style={{ color: T.soft }}>—</span></td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>{requestStatusPill(r.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ),
            )
          )}
        {tab === "history" ? (
          <span style={{ fontSize: 11.5, color: T.soft }}>
            {t("workforce.requests.historyHeld", "Who decided each request — and whether the marketplace settled it rather than a person — isn't served, so the column reads “—” rather than crediting the manager (REQ-101c). The retention window isn't stated by the API either.")}
          </span>
        ) : null}
      </div>
    </Screen>
  );
}
